import { useEffect, useMemo } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import AccountingToolPage from '@/components/AccountingToolPage'
import { usePageAccess } from '@/hooks/usePageAccess'
import { REPORTING_BASE, reportingMembersPath } from './reportingPaths'

const LEGACY_SUBTAB_KEY = 'monthlyPerformance.subtab'
const SUBTAB_KEY = 'reporting.subtab'
const SUBTABS = [
  { id: 'report', label: 'Report' },
  { id: 'settings', label: 'Settings' },
] as const

type Subtab = (typeof SUBTABS)[number]['id']

function readStoredSubtab(): Subtab {
  try {
    let stored = localStorage.getItem(SUBTAB_KEY)
    if (!stored) {
      stored = localStorage.getItem(LEGACY_SUBTAB_KEY)
      if (stored === 'report' || stored === 'settings') {
        localStorage.setItem(SUBTAB_KEY, stored)
        try {
          localStorage.removeItem(LEGACY_SUBTAB_KEY)
        } catch {
          // ignore
        }
      }
    }
    if (stored === 'report' || stored === 'settings') return stored
  } catch {
    // ignore
  }
  return 'report'
}

export function MonthlyPerformanceIndexRedirect() {
  const { canEdit } = usePageAccess('monthly-performance')
  if (!canEdit) {
    return <Navigate to={reportingMembersPath()} replace />
  }
  const subtab = readStoredSubtab()
  return <Navigate to={subtab} replace />
}

function SegmentedToggle({
  activeId,
  onChange,
}: {
  activeId: Subtab
  onChange: (id: Subtab) => void
}) {
  return (
    <div
      className="backfill-audit-segmented-toggle inline-flex rounded-lg bg-gray-200 p-0.5"
      role="group"
      aria-label="Reporting sections"
      data-bk-nav
    >
      {SUBTABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onChange(tab.id)}
          className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
            activeId === tab.id
              ? 'bg-white text-brand-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

export default function MonthlyPerformanceLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { canEdit } = usePageAccess('monthly-performance')

  const activeId = useMemo(() => {
    const segment = location.pathname.split('/').pop()
    if (segment === 'report' || segment === 'settings') return segment
    return 'report'
  }, [location.pathname])

  useEffect(() => {
    const segment = location.pathname.split('/').pop()
    if (segment === 'report' || segment === 'settings') {
      try {
        localStorage.setItem(SUBTAB_KEY, segment)
      } catch {
        // ignore
      }
    }
  }, [location.pathname])

  const segment = location.pathname.split('/').pop()
  if (!canEdit && (segment === 'report' || segment === 'settings')) {
    return <Navigate to={reportingMembersPath()} replace />
  }

  return (
    <AccountingToolPage
      title="Reporting"
      description="Generate and send performance summary emails"
      scrollContent={activeId !== 'report'}
      headerExtra={
        <SegmentedToggle
          activeId={activeId}
          onChange={(id) => navigate(`${REPORTING_BASE}/${id}`)}
        />
      }
    >
      <Outlet />
    </AccountingToolPage>
  )
}
